import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchRequestQuickDetail } from "../../../../api/t2tRequestsApi";
import RequestDetails        from "./RequestDetails";
import StageHistoryTimeline  from "./StageHistoryTimeline";
import FormFieldsExpander    from "./FormFieldsExpander";
import AISummaryBlock        from "./AISummaryBlock";
import EditRequestModal      from "./EditRequestModal";
import EmbeddedChat          from "./EmbeddedChat";
import { getSlaStatus }      from "../slaUtils";

const TABS = [
  { key: "details",  label: "Details",  icon: "bi-info-circle" },
  { key: "history",  label: "History",  icon: "bi-clock-history" },
  { key: "chat",     label: "Chat",     icon: "bi-chat-dots" },
];

const isMobile = () => window.innerWidth <= 576;

export default function SlideOverPanel({ requestId, initialTab = "details", onClose }) {
  const navigate   = useNavigate();
  const authUser   = useSelector((state) => state?.user?.users?.[0]);
  const isAdmin    = !!(authUser?.t2t_role_name || "").toLowerCase().includes("admin");

  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [tab,       setTab]       = useState(initialTab);
  const [mobile,    setMobile]    = useState(isMobile);
  const [editOpen,  setEditOpen]  = useState(false);
  const touchStartY = useRef(null);

  useEffect(() => {
    const handle = () => setMobile(isMobile());
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  // T2T-05-C5: when initialTab changes (e.g. user clicks a chat notification
  // while the slide-over is already open), jump to the requested tab.
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd   = (e) => {
    if (touchStartY.current === null) return;
    const delta = e.changedTouches[0].clientY - touchStartY.current;
    if (delta > 80) onClose();
    touchStartY.current = null;
  };

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    const resp = await fetchRequestQuickDetail(requestId);
    if (resp.error) {
      setError("Could not load request details.");
    } else {
      const raw = resp.data?.data;
      if (raw && !raw.sla) raw.sla = getSlaStatus(raw.due_at, raw.sla_minutes);
      setData(raw);
    }
    setLoading(false);
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && !editOpen) onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, editOpen]);

  return (
    <>
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{ background: "rgba(0,0,0,0.25)", zIndex: 1040 }}
        onClick={onClose}
      />

      <div
        className="position-fixed d-flex flex-column"
        style={mobile ? {
          bottom: 0, left: 0, right: 0,
          height: "85dvh", borderRadius: "16px 16px 0 0",
          zIndex: 1050,
          background: "var(--t2t-surface)",
          boxShadow: "0 -4px 32px rgba(0,0,0,0.14)",
        } : {
          top: 0, right: 0, height: "100%",
          width: "min(480px, 100vw)", zIndex: 1050,
          background: "var(--t2t-surface)",
          boxShadow: "-4px 0 32px rgba(0,0,0,0.12)",
        }}
        onTouchStart={mobile ? handleTouchStart : undefined}
        onTouchEnd={mobile   ? handleTouchEnd   : undefined}
      >
        {mobile && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--t2t-border)" }} />
          </div>
        )}

        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: "12px 20px", borderBottom: "1px solid var(--t2t-border)", background: "var(--t2t-surface)", flexShrink: 0 }}
        >
          <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
            {data && (
              <span
                className="font-monospace"
                style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-primary)", background: "var(--t2t-primary-light, rgba(42,99,234,0.08))", borderRadius: 4, padding: "2px 7px", marginRight: 8, display: "inline-block" }}
              >
                {data.ref_id}
              </span>
            )}
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--t2t-text-primary)" }}>
              {loading ? "Loading…" : data?.title || "Request Detail"}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {data && (
              <button
                className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                title="Open full detail"
                onClick={() => navigate(`/t2t/request-detail/${data.request_id}`)}
              >
                <i className="bi bi-box-arrow-up-right" style={{ fontSize: 13 }} />
              </button>
            )}
            {data && isAdmin && (
              <button
                className="t2t-btn t2t-btn-outline t2t-btn-sm d-flex align-items-center gap-1"
                onClick={() => setEditOpen(true)}
              >
                <i className="bi bi-pencil" style={{ fontSize: 11 }} />
                Edit
              </button>
            )}
            <button
              className="t2t-btn t2t-btn-ghost t2t-btn-sm"
              onClick={onClose}
              aria-label="Close"
              style={{ fontSize: 16, lineHeight: 1, padding: "4px 8px" }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: "0 20px", borderBottom: "1px solid var(--t2t-border)", background: "var(--t2t-surface)", flexShrink: 0 }}>
          <div className="t2t-tab-group" style={{ background: "none", padding: "8px 0 0", borderRadius: 0, gap: 0 }}>
            {TABS.map(t => (
              <button
                key={t.key}
                className={`t2t-tab d-flex align-items-center gap-1${tab === t.key ? " active" : ""}`}
                style={tab === t.key ? { borderBottom: "2px solid var(--t2t-primary)", borderRadius: "6px 6px 0 0" } : { borderRadius: "6px 6px 0 0" }}
                onClick={() => setTab(t.key)}
              >
                <i className={`bi ${t.icon}`} style={{ fontSize: 11 }} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", background: "var(--t2t-bg)" }}>
          {loading && (
            <div>
              {[120, 80, 100, 60].map((w, i) => (
                <div key={i} className="kb-skeleton-bar" style={{ width: `${w}%`, height: 14, marginBottom: 12, borderRadius: 6 }} />
              ))}
            </div>
          )}
          {error && !loading && (
            <div
              style={{ background: "#FEE2E2", color: "#DC2626", border: "1px solid #FECACA", borderRadius: "var(--t2t-radius)", padding: "10px 14px", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}
            >
              <i className="bi bi-exclamation-triangle-fill" />
              <span style={{ flex: 1 }}>{error}</span>
              <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={load} style={{ color: "#DC2626" }}>Retry</button>
            </div>
          )}

          {data && !loading && (
            <>
              {tab === "details" && (
                <>
                  <AISummaryBlock
                    requestId={data.request_id}
                    existingSummary={data.ai_summary}
                    onSummaryGenerated={(text) => setData(d => ({ ...d, ai_summary: text }))}
                  />
                  <p className="t2t-section-label mb-3">Details</p>
                  <RequestDetails request={data} />
                  <div className="mt-4 mb-3">
                    <FormFieldsExpander
                      formData={data.form_data}
                      requestTypeFields={data.request_type_fields}
                      workflowFormFields={data.workflow_form_fields}
                    />
                  </div>
                </>
              )}

              {tab === "history" && (
                <>
                  <p className="t2t-section-label mb-3">Stage History</p>
                  <StageHistoryTimeline history={data.stage_history} />
                </>
              )}

              {tab === "chat" && (
                <EmbeddedChat requestId={data.request_id} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit modal — rendered above slide panel (z-index 1060) */}
      {editOpen && data && (
        <EditRequestModal
          request={data}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}
    </>
  );
}

SlideOverPanel.propTypes = {
  requestId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialTab: PropTypes.string,
  onClose:    PropTypes.func.isRequired,
};
